import { useContext, useMemo, useState } from 'react'

import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import classNames from 'classnames'

import { ProfileImageType } from '~/components/atoms/ProfileImage/ProfileImage'
import SearchAbleSelect from '~/components/atoms/SearchAbleSelect/SearchAbleSelect'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useDebounce } from '~/services/useDebounce'
import EsIndexTypes = indexes.EsIndexTypes
import RecordTypes = indexes.RecordTypes

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import { SelectedArtworkContext } from '~/contexts/SelectedArtworkContext'
import { CurrentArtworkProps } from '~/contexts/SelectedArtworkReducer'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import useFormAnalytics from '~/hooks/useFormAnalytics'
import { useSelectbox } from '~/services/query'
import { PushInteractionType } from '~/utils/posthogEvents'

import styles from './ArtworkSearchSelect.module.css'

export interface ArtworkSearchSelectProps {
  pageSize?: number
  filters?: any[]
  onEnterManually?: () => void
  name: string
  value?: string | null
  onChange: (item: any | null) => void
}

type EmptyArtistArtworkSelectProps = {
  data?: any[]
  searchTerm?: string
  handleNoArtwork?: () => void
  showEnterManually?: boolean
  description?: string
  title?: string
  useDynamic?: boolean
  withinSearchResults?: boolean
  isRelative?: boolean
}

export const EmptyArtistArtworkSelect = ({
  data,
  searchTerm,
  handleNoArtwork,
  showEnterManually,
  title = 'No Results',
  description = `If your artwork isn't listed, our specialists can provide a tailored valuation, or recommendation.`,
  useDynamic = false,
  withinSearchResults = false,
  isRelative = false,
}: EmptyArtistArtworkSelectProps) => {
  if (!useDynamic && searchTerm && data && data.length > 0) {
    return null
  }

  return (
    <div
      className={classNames(styles.childEl, {
        [styles.withinSearchResults]: withinSearchResults,
        [styles.isRelative]: isRelative,
      })}
    >
      <div
        className={classNames(styles.cantFind, {
          [styles.show]: searchTerm || showEnterManually,
        })}
      >
        <Text
          size={TextSizes.primaryTitleMed}
          className={styles.noResultsTitle}
          Tag="p"
        >
          {title}
        </Text>
        <Text size={TextSizes.bodyMed} className={styles.noResultsText} Tag="p">
          {description}{' '}
          <Button
            launchForm={FormTypes.valuation}
            button={{ type: ButtonTypes.button }}
            variant={ButtonVariants.noStyles}
          >
            Click here
          </Button>{' '}
          to get started.
        </Text>
      </div>
    </div>
  )
}

export default function ArtworkSearchSelect({
  name,
  value,
  onChange,
  pageSize = 1500,
  filters = [],
  onEnterManually,
  ...restProps
}: ArtworkSearchSelectProps) {
  const t = useTranslations('common')
  const { currentArtworkState, currentArtworkDispatch } = useContext(
    SelectedArtworkContext
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [debounced] = useDebounce(searchTerm)

  const { data } = useSelectbox(
    debounced,
    EsIndexTypes.ARTWORKS,
    RecordTypes.ALL_ARTWORK_SELECT_LIST,
    1500,
    [
      {
        term: {
          artistId: currentArtworkState.artist.id,
        },
      },
    ]
  )

  const { handleFieldBlurAnalytics, handleSearchableSelectionAnalytics } =
    useFormAnalytics()

  // flatten & filter out any undefined values
  const options = useMemo(() => {
    return (data || [])
      .reduce((accumulator: any, value: any) => accumulator.concat(value), [])
      .sort((a: any, b: any) => {
        if (searchTerm) return true
        const nameA = a.label
        const nameB = b.label
        return nameA?.localeCompare(nameB)
      })
      .filter((item: any) => {
        return item.value !== undefined
      })
  }, [data])

  const setCurrentArt = (payload: any) =>
    currentArtworkDispatch({
      type: CurrentArtworkProps.UPDATE_SELECTED_ARTWORK,
      payload,
    })

  const handleSelect = (label: string, val: string) => {
    const artworkItem = data?.find(
      (item: any) => item.value === val || item.label === label
    )

    if (artworkItem) {
      setCurrentArt({
        selectedArtwork: {
          name: artworkItem.label,
          id: artworkItem.value,
        },
      })

      onChange(artworkItem)
    } else {
      onChange(null)
    }

    handleSearchableSelectionAnalytics(
      'Selected Artwork',
      PushInteractionType.LeftInputField,
      FormTypes.valuation,
      label
    )
  }

  const handleInputChange = (val: string) => {
    setSearchTerm(val)
  }

  const handleNoArtwork = () => {
    onChange(null)
    onEnterManually?.()
  }

  const onFocus = () => {} // if you need to track focus
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) =>
    handleFieldBlurAnalytics('Artwork Search Blur', e)

  return (
    <SearchAbleSelect
      {...restProps}
      type={ProfileImageType.Artwork}
      name={name}
      value={value ?? searchTerm}
      searchTerm={searchTerm}
      options={options}
      placeholder={t('searchArtworkName')}
      onChange={handleSelect}
      inputChange={handleInputChange}
      showToggleMenu={false}
      onFocus={onFocus}
      onBlurHandler={onBlur}
      reducedHeight
      hidePreMenuTitle
      zIndex={5}
      childEl={
        <EmptyArtistArtworkSelect
          data={data}
          searchTerm={searchTerm}
          handleNoArtwork={handleNoArtwork}
          showEnterManually={false}
        />
      }
    />
  )
}
